/**
 * Firebase Configuration
 *
 * All config values are loaded from environment variables.
 * CodeCanyon buyers should set their own Firebase config in .env
 */

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';

// Firebase config from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Check if Firebase is configured
const isFirebaseConfigured = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.appId
  );
};

// Initialize Firebase app (singleton)
let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;

export const initializeFirebase = async (): Promise<void> => {
  if (!isFirebaseConfigured()) {
    console.log('[Firebase] Not configured - skipping initialization');
    return;
  }

  if (app) {
    return; // Already initialized
  }

  try {
    app = initializeApp(firebaseConfig);

    // Initialize Analytics only if supported (not in SSR, etc.)
    const analyticsSupported = await isSupported();
    if (analyticsSupported) {
      analytics = getAnalytics(app);
      console.log('[Firebase] Analytics initialized');
    }
  } catch (error) {
    console.error('[Firebase] Initialization error:', error);
  }
};

export const getFirebaseApp = (): FirebaseApp | null => app;
export const getFirebaseAnalytics = (): Analytics | null => analytics;

export { isFirebaseConfigured };
